import { calculateHeaderGroupHeight } from '@theme/critical';
import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { onDocumentLoaded, changeMetaThemeColor } from '@theme/utilities';
import './sticky-position-manager.js';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * While the root mobile `header-drawer` is open: ignore window scroll (body is `position:fixed`
   * and `scrollY` is 0; also avoids spurious events). Force `data-sticky-state="active"` so the bar
   * stays in the sticky layout. (Do not use `scroll-lock` on the menu &lt;details&gt;: `overflow:hidden`
   * on &lt;html&gt; breaks `position:sticky` for the header.)
   * @type {boolean}
   */
  #mobileDrawerOpen = false;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'
   * @constant {number}
   */
  #animationDelay = 150;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const { height } = entry.target.getBoundingClientRect();
    document.body.style.setProperty('--header-height', `${height}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        changeMetaThemeColor(this.refs.headerRowTop);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (!this.refs.headerMenu || !this.refs.headerDrawerContainer) return;
    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove('desktop:hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add('hidden');
    } else {
      this.refs.headerDrawerContainer.classList.add('desktop:hidden');
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove('hidden');
    }
  }

  /**
   * Viewport scroll position (use `window` — iOS Safari often does not emit document scroll).
   * @returns {number}
   */
  #viewportScrollTop() {
    return window.scrollY ?? document.documentElement.scrollTop ?? document.body.scrollTop ?? 0;
  }

  /**
   * @param {Event} event
   */
  #onMobileDrawerEvent = (event) => {
    if (!(event instanceof CustomEvent)) return;

    const open = event.detail?.open === true;
    this.#mobileDrawerOpen = open;

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode !== 'scroll-up') return;

    if (open) {
      this.#lastScrollTop = this.#viewportScrollTop();
      this.setAttribute('data-sticky-state', 'active');
    } else {
      this.syncScrollState();
    }
  };

  #handleWindowScroll = () => {
    const stickyMode = this.getAttribute('sticky');
    if (stickyMode === 'always') {
      this.setAttribute('data-sticky-state', 'active');
      return;
    }

    if (this.#mobileDrawerOpen) return;

    if (stickyMode === 'scroll-up') {
      const scrollTop = this.#viewportScrollTop();
      const scrollDelta = scrollTop - this.#lastScrollTop;

      // Only update if there's significant scroll movement
      if (Math.abs(scrollDelta) < 10) return;

      const isScrollingUp = scrollDelta < 0;
      const isAtTop = scrollTop <= 1;

      if (isAtTop) {
        // When at absolute top, remove state - header in normal position, visible
        this.removeAttribute('data-sticky-state');
      } else if (isScrollingUp) {
        // When scrolling up, show sticky header
        this.setAttribute('data-sticky-state', 'active');
      } else {
        // When scrolling down, hide header
        this.setAttribute('data-sticky-state', 'inactive');
      }

      this.#lastScrollTop = scrollTop;
    }
  };

  /**
   * Resynchronizes the header's internal scroll tracking with the current scroll position.
   *
   * Needed after a modal/drawer (cart drawer, search modal, etc.) closes: while the dialog is
   * open the body is set to `position: fixed; top: -scrollY`, which collapses the document
   * scrollTop to 0. When the dialog closes and scroll is restored via `window.scrollTo`, the
   * header's `#lastScrollTop` is still pinned to 0, so the very first user scroll-up is
   * misinterpreted as a scroll-down and the header stays hidden until refresh.
   */
  syncScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    if (stickyMode !== 'scroll-up') return;

    const scrollTop = this.#viewportScrollTop();
    this.#lastScrollTop = scrollTop;

    if (scrollTop <= 1) {
      this.removeAttribute('data-sticky-state');
    } else {
      // After a drawer/modal closes we want the header visible at its restored scroll position.
      this.setAttribute('data-sticky-state', 'active');
    }
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.addEventListener(ThemeEvents.headerMobileDrawer, this.#onMobileDrawerEvent);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      // Only register 'always' mode with position manager
      // For 'scroll-up', let the scroll handler manage state directly
      if (stickyMode === 'always') {
        this.#registerWithPositionManager(stickyMode);
      }

      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        window.addEventListener('scroll', this.#handleWindowScroll, { passive: true });
      }
    }
  }

  /**
   * @param {string} stickyMode
   */
  #registerWithPositionManager(stickyMode) {
    // Wait for position manager to be available
    const register = () => {
      if (/** @type {any} */ (window).stickyPositionManager) {
        /** @type {any} */ (window).stickyPositionManager.register('header', this, {
          priority: 100, // High priority - header should be at top when visible
          stickyMode: stickyMode,
          onPositionChange: (/** @type {any} */ position) => {
            this.style.top = `${position.top}px`;
            this.style.zIndex = position.zIndex;
          },
          onStickyStateChange: (/** @type {boolean} */ isSticky) => {
            this.dataset.stickyState = isSticky ? 'active' : 'inactive';
            changeMetaThemeColor(this.refs.headerRowTop);
          }
        });
      } else {
        setTimeout(register, 50);
      }
    };

    register();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener(ThemeEvents.headerMobileDrawer, this.#onMobileDrawerEvent);
    window.removeEventListener('scroll', this.#handleWindowScroll);
    document.body.style.setProperty('--header-height', '0px');

    // Unregister from position manager
    if (/** @type {any} */ (window).stickyPositionManager) {
      /** @type {any} */ (window).stickyPositionManager.unregister('header');
    }
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');

  // Update header group height on resize of any child (must write CSS var — calculate alone was a no-op).
  if (headerGroup) {
    const syncHeaderGroupHeight = () => {
      const h = calculateHeaderGroupHeight(header, headerGroup);
      document.body.style.setProperty('--header-group-height', `${h}px`);
    };
    syncHeaderGroupHeight();
    const resizeObserver = new ResizeObserver(syncHeaderGroupHeight);

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element === header || !(element instanceof HTMLElement)) continue;
      resizeObserver.observe(element);
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element === header || !(element instanceof HTMLElement)) continue;
            resizeObserver.observe(element);
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
